import { useState } from 'react'

import { ALGOLIA_INDEX_SUFFIX } from 'env'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { filterPrivateWorkspaces } from 'utils/workspaceUtils'
import { useAuth } from 'hooks/UseAuth'

import { WorkspaceSelector } from './components/WorkspaceSelector/WorkspaceSelector'
import { FileTreeLocationPicker } from './components/FileTreeLocationPicker/FileTreeLocationPicker'
import Styles from './LocationPicker.module.scss'

import { CustomHits as Hits } from './components/Hits/Hits'
import { CustomSearchInput as SearchInput } from './components/SearchInput/SearchInput'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import { AncestorProject, ViewType } from 'types'
import { useLocationPicker } from './hooks/useLocationPicker'
import { getIndexFilters } from 'screens/blaze/Search/SearchModal/components/useSearch'
import { Search } from 'blaze-ui/icons'

export interface LocationPickerFolder {
  id: string
  title: string
  ancestor_keys: string[]
  ancestor_projects?: AncestorProject[]
  default_view?: ViewType
  database_mode?: boolean
  header_emoji?: string | null
  project_id?: number
}

export interface SelectedFolder {
  id: string
  title: string
  seo_string: string
  ancestor_projects?: AncestorProject[]
  handbook_key: string
  default_view?: string
  database_mode?: boolean
  header_emoji?: string | null
  project_id?: number
}

export interface Props {
  actionType: 'copy' | 'move' | 'create' | 'search'
  initialFolder: LocationPickerFolder | null | undefined
  onSelectFolder: ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: SelectedFolder | null
    newWorkspaceId: number
  }) => void
  initialWorkspaceId?: number
  isRootDisabled: boolean
  isCreateDisabled?: boolean
  disabledFolderIds?: string[]
  isWorkspaceSelectorEnabled?: boolean
  hideIcon?: boolean
  keepIconInMobile?: boolean
  fileTitle?: string
}

export const LocationPicker = ({
  actionType,
  initialFolder,
  onSelectFolder,
  initialWorkspaceId,
  isRootDisabled = true,
  isCreateDisabled = false,
  disabledFolderIds = [],
  isWorkspaceSelectorEnabled = false,
  hideIcon = false,
  keepIconInMobile = false,
  fileTitle,
}: Props) => {
  const {
    handleHitClick,
    onSelect,
    searchInputValue,
    setSearchInputValue,
    activeFolder,
    setActiveFolder,
    searchPlaceholder,
  } = useLocationPicker({
    actionType,
    initialFolder,
    onSelectFolder,
    initialWorkspaceId,
    fileTitle,
  })
  const auth = useAuth()
  const workspaces = useWorkspaces()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const privateWorkspaces = filterPrivateWorkspaces(workspaces)
  const [workspaceId, setWorkspaceId] = useState(initialWorkspaceId || currentWorkspaceId)
  const client = useAlgoliaClient()

  if (!auth || !workspaceId) return null

  return (
    <div className={Styles.container}>
      <InstantSearch searchClient={client} indexName={`Article_${ALGOLIA_INDEX_SUFFIX}`}>
        <Configure hitsPerPage={20} filters={`${getIndexFilters({ workspaceId, auth })} AND project_index = 1`} />
        <div className={Styles.header}>
          <SearchInput
            icon={Search}
            value={searchInputValue}
            setValue={setSearchInputValue}
            placeholder={searchPlaceholder}
            hideIcon={hideIcon}
            keepIconInMobile={keepIconInMobile}
          />
        </div>
        <div className={Styles.body}>
          {searchInputValue.length === 0 ? (
            <FileTreeLocationPicker
              workspaceId={workspaceId}
              onSelect={onSelect}
              activeFolder={activeFolder}
              isRootDisabled={isRootDisabled}
              isCreateDisabled={isCreateDisabled}
              disabledFolderIds={disabledFolderIds}
            />
          ) : (
            <Hits activeFolderId={activeFolder?.id} onClick={handleHitClick} disabledFolderIds={disabledFolderIds} />
          )}
        </div>
      </InstantSearch>
      {isWorkspaceSelectorEnabled && (
        <div className={Styles.footer}>
          <WorkspaceSelector
            workspaces={privateWorkspaces}
            workspaceId={workspaceId}
            onWorkspaceSelect={(id: number) => {
              setActiveFolder(null)
              setWorkspaceId(id)
            }}
          />
        </div>
      )}
    </div>
  )
}
